import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Sidebar } from "@/components/layout/sidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Screenplay, RenderJob } from "@/lib/user-types";
import { Skeleton } from "@/components/ui/skeleton";
import { RenderCard } from "@/components/render-card";
import { CharacterManager } from "@/components/character-manager";
import { FileText, Download, Plus, View } from "lucide-react";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { S3_URL_PREFIX } from "@/lib/environment";

export default function ScreenplayPage() {
  const { id } = useParams();
  const [, navigate] = useLocation();
  const [s3UrlPrefix, setS3UrlPrefix] = useState('');
  
  // Fetch the S3 URL prefix
  useEffect(() => {
    async function fetchS3UrlPrefix() {
      try {
        const prefix = await S3_URL_PREFIX.get();
        setS3UrlPrefix(prefix);
      } catch (error) {
        console.error('Failed to fetch S3 URL prefix:', error);
      }
    }
    
    fetchS3UrlPrefix();
  }, []);
  
  const screenplayId = id || "";
  
  // Fetch screenplay details
  const { 
    data: screenplay, 
    isLoading: isLoadingScreenplay 
  } = useQuery<Screenplay>({
    queryKey: [`/api/screenplays/${screenplayId}`],
    enabled: !!screenplayId,
  });
  
  // Fetch renders for this screenplay
  const { 
    data: renderJobs, 
    isLoading: isLoadingRenderJobs 
  } = useQuery<RenderJob[]>({
    queryKey: [`/api/screenplays/${screenplayId}/renders`],
    enabled: !!screenplayId,
  });
  
  const handleReRender = () => {
    // Make sure we use the same path format as defined in App.tsx
    navigate(`/prep-render/${screenplayId}`);
  };
  
  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 ml-0 md:ml-64">
        <main className="py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            {isLoadingScreenplay ? (
              <div className="flex items-center space-x-4">
                <Skeleton className="h-8 w-64" />
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">{screenplay?.title}</h1>
                <div className="mt-2 sm:mt-0">
                  <Button variant="outline" onClick={handleReRender} className="mr-2">
                    <Plus className="h-4 w-4 mr-2" />
                    Create New Render
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            <div className="py-4 space-y-6">
              {/* Screenplay Details */}
              <Card>
                <CardHeader>
                  <CardTitle>Screenplay Details</CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingScreenplay ? (
                    <ScreenplayDetailsSkeleton />
                  ) : screenplay ? (
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <FileText className="h-10 w-10 text-primary-600 mr-4" />
                        <div>
                          <h3 className="text-lg font-medium">{`${screenplay.title} (${screenplay.filePath.slice(-4)})`}</h3>
                          <p className="text-sm text-gray-500">
                            Uploaded on {format(new Date(screenplay.uploadedAt), "MMMM d, yyyy")}
                          </p>
                          
                          <div className="mt-4">
                            <Button variant="outline" size="sm" asChild>
                              <a 
                                href={`${s3UrlPrefix}${screenplay.filePath}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                              >
                                <View className="h-4 w-4 mr-2" />
                                View File
                              </a>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-500">Screenplay not found</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Renderings */}
              <Card>
                <CardHeader>
                  <CardTitle>Renderings</CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingRenderJobs ? (
                    <div className="space-y-4">
                      {[1, 2].map((i) => (
                        <RenderListItemSkeleton key={i} />
                      ))}
                    </div>
                  ) : renderJobs && renderJobs.length > 0 ? (
                    <ul role="list" className="-my-5 divide-y divide-gray-200">
                      {renderJobs.map((renderJob) => (
                        <RenderCard key={renderJob.id} renderJob={renderJob} />
                      ))}
                    </ul>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No renders have been created for this screenplay yet.</p>
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={handleReRender}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create your first render
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Characters */}
              <Card>
                <CardHeader>
                  <CardTitle>Characters</CardTitle>
                </CardHeader>
                <CardContent>
                  <CharacterManager screenplayId={screenplayId} screenplay={screenplay} />
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function ScreenplayDetailsSkeleton() {
  return (
    <div className="flex items-start">
      <Skeleton className="h-10 w-10 rounded mr-4" />
      <div className="space-y-2 flex-1">
        <Skeleton className="h-6 w-52" />
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-9 w-36 mt-4" />
      </div>
    </div>
  );
}

function RenderListItemSkeleton() {
  return (
    <div className="py-4">
      <div className="flex items-center space-x-4">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1 min-w-0">
          <Skeleton className="h-5 w-3/4 mb-2" />
          <div className="flex items-center">
            <Skeleton className="h-4 w-16 rounded-full" />
            <Skeleton className="h-4 w-24 rounded-full ml-2" />
          </div>
        </div>
        <Skeleton className="h-8 w-16 rounded-full" />
      </div>
    </div>
  );
}
